import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLayout } from '../contexts/layout/LayoutContext';
import useStorage from './useStorage';
import Modal from '../ui/modal/Modal';
import useLabels from './useLabels';
import useDebounce from './useDebounce';
import ChatDisclaimer, { type ChatDisclaimerProps } from '../elements/chat-disclaimer/ChatDisclaimer';
import ChatDisclaimerModalFooter from '../elements/chat-disclaimer-modal-footer/ChatDisclaimerModalFooter';
import MarkdownWrapper from '../ui/markdown-wrapper/MarkdownWrapper';

const storeScope = 'chat-window';
const disclaimerStoreKey = 'is-disclaimer-read';

export default function useDisclaimer() {
    const labels = useLabels();
    const l = useMemo(() => ({ hl: labels.disclaimerHeadline, cta: labels.disclaimerButton }), [labels.disclaimerButton, labels.disclaimerHeadline]);
    const { isShowingTermsAndConditions, setIsShowingTermsAndConditions } = useLayout();
    const [isDisclaimerRead, setIsDisclaimerRead] = useState<boolean>(false);

    const { get, set } = useStorage(storeScope, 'session');

    useEffect(() => {
        const isRead = get(disclaimerStoreKey);
        setIsShowingTermsAndConditions(!isRead);
    }, [get, setIsShowingTermsAndConditions]);

    const hideDisclaimer = useCallback(() => {
        set(disclaimerStoreKey, true);
        setIsShowingTermsAndConditions(false);
    }, [set, setIsShowingTermsAndConditions]);

    // Debounce to allow modal to animate out
    const debouncedShowDisclaimer = useDebounce(isShowingTermsAndConditions, 300, isShowingTermsAndConditions);
    const renderDisclaimer = useCallback((props: Omit<ChatDisclaimerProps, 'hideButton' | 'onCtaClick' | 'onDisclaimerIsRead'> = {}) => {
        if (!debouncedShowDisclaimer) return null;
        const disclaimer = isShowingTermsAndConditions && <ChatDisclaimer {...props} onDisclaimerIsRead={setIsDisclaimerRead} />;
        return <Modal
            size='large'
            headline={l.hl}
            hideCloseIcon
            isOpen={isShowingTermsAndConditions}
            body={disclaimer}
            footer={<ChatDisclaimerModalFooter extra={<MarkdownWrapper markdown={labels.disclaimerConfirmationMarkdown} />} button={{ label: l.cta, onClick: hideDisclaimer, isDisabled: !isDisclaimerRead }} />}
        />;
    }, [debouncedShowDisclaimer, isShowingTermsAndConditions, l.hl, l.cta, labels.disclaimerConfirmationMarkdown, hideDisclaimer, isDisclaimerRead]);

    return {
        showDisclaimer: isShowingTermsAndConditions,
        isDisclaimerRead,
        renderDisclaimer,
    };
}